// Realtime API が response.output_audio.delta で送ってくる 24kHz PCM16 を継ぎ目なく鳴らす
// (spec 2026-08-29 §3.2)。既存の TtsPlayer は mp3 の Blob URL を <audio> で鳴らすものなので
// 使えない。
//
// **要点は「次のチャンクを前のチャンクの終端に継ぐ」こと。** 毎回 ctx.currentTime から
// 鳴らすと、チャンクの到着が再生より速いときに音が重なり、遅いときに途切れる。
//
// WebAudio を作れない環境では黙って何もしない (原則 5 — 音のために本編を止めない)。
// processingCue.ts / testTone.ts と同じ流儀。

const RATE = 24000;

export class PcmStreamPlayer {
  private ctx: AudioContext | null = null;
  private ctxFailed = false;
  /** 次のチャンクを鳴らし始める時刻 (AudioContext の時間軸)。 */
  private nextAt = 0;
  private sources = new Set<AudioBufferSourceNode>();

  private ensure(): AudioContext | null {
    if (this.ctx || this.ctxFailed) return this.ctx;
    try {
      const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (!Ctor) {
        this.ctxFailed = true;
        return null;
      }
      this.ctx = new Ctor();
    } catch {
      this.ctxFailed = true;
      return null;
    }
    return this.ctx;
  }

  /**
   * ユーザー操作のコールスタック内で呼び、AudioContext を鳴らせる状態にする。
   *
   * **iOS (WebKit) の autoplay 許可はユーザー操作の文脈でしか得られない。**
   * TtsPlayer は無音 WAV を <audio> で鳴らして取っているが (2026-08-04 の実測
   * T1/T2/T3)、AudioContext は別の器なので同じ許可を受けていない。**マイク許可の
   * タップから、最初の await より前に呼ぶこと** — 後ろに置くと文脈が切れ、
   * iPhone では realtime の声が 1 つも鳴らない。
   *
   * resume() だけでなく無音を 1 サンプル鳴らすのは、resume の解決を待つ間に
   * 文脈が切れる実装があるため。**再生予定 (nextAt) は進めない**ので、直後の
   * enqueue は現在時刻から鳴る。
   */
  unlock(): void {
    const ctx = this.ensure();
    if (!ctx) return;
    try {
      const p = ctx.resume?.();
      if (p && typeof p.catch === "function") p.catch(() => undefined);
      const buf = ctx.createBuffer(1, 1, RATE);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(ctx.currentTime);
    } catch {
      // 鳴らせなくても本編は続ける (原則 5)
    }
  }

  /** 24kHz mono PCM16 のチャンクを再生キューへ積む。 */
  enqueue(pcm16: Int16Array): void {
    const ctx = this.ensure();
    if (!ctx || pcm16.length === 0) return;
    try {
      const buf = ctx.createBuffer(1, pcm16.length, RATE);
      const ch = buf.getChannelData(0);
      // PCM16 → float。負は 0x8000、正は 0x7fff で割る (floatToBase64Pcm16 の逆)
      for (let i = 0; i < pcm16.length; i++) {
        const v = pcm16[i];
        ch[i] = v < 0 ? v / 0x8000 : v / 0x7fff;
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      // 予定より現在時刻が進んでいたら切り上げる。過去の時刻を渡すと WebAudio は
      // 即時再生するが、予定表がずれたまま積み上がって以降ずっと重なる。
      const startAt = Math.max(ctx.currentTime, this.nextAt);
      src.start(startAt);
      this.nextAt = startAt + buf.duration;
      this.sources.add(src);
      src.onended = () => {
        this.sources.delete(src);
      };
    } catch {
      // 鳴らせなくても本編は続ける
    }
  }

  /** 鳴っている音を全部止める (割り込み)。次の enqueue は現在時刻から鳴る。 */
  stop(): void {
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        // 既に停止済み
      }
    }
    this.sources.clear();
    this.nextAt = 0;
  }

  isPlaying(): boolean {
    return this.sources.size > 0;
  }
}
